import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReportType } from '@finvox/report-engine';
import { getFirestoreDb } from '@/lib/firebase';
import { isValidEmail } from '@/lib/emailValidation';
import { useToast } from '@/contexts/ToastContext';
import { useAsyncSave } from '@/components/settings/useAsyncSave';
import { useOrgSettingsDoc } from '@/components/settings/useOrgSettingsDoc';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { SettingsFirestoreError } from '@/components/settings/SettingsFirestoreError';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { SettingsSection } from '@/components/settings/SettingsSection';

const SCHEDULE_REPORT_TYPES: ReportType[] = [
  'trial_balance',
  'profit_loss',
  'balance_sheet',
  'ar_aging',
  'ap_aging'
];

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

export function ReportScheduleSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { orgId, online, canEdit } = useSettingsAccess();
  const { raw, loading, error } = useOrgSettingsDoc(orgId, 'reportSchedule');
  const { saving, run } = useAsyncSave('settings-report-schedule');
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [reportType, setReportType] = useState<ReportType>('trial_balance');
  const [dayOfWeek, setDayOfWeek] = useState(1);

  useEffect(() => {
    if (!raw) return;
    setEnabled(Boolean(raw.enabled));
    setEmail(String(raw.email ?? ''));
    if (raw.reportType) setReportType(raw.reportType as ReportType);
    if (typeof raw.dayOfWeek === 'number') setDayOfWeek(raw.dayOfWeek);
  }, [raw]);

  async function save() {
    if (!orgId || !canEdit || !online) return;
    if (enabled && !isValidEmail(email)) {
      toast.error(t('settings.reportScheduleInvalidEmail'), 'settings-report-schedule');
      return;
    }
    await run(async () => {
      await setDoc(
        doc(getFirestoreDb(), 'organizations', orgId, 'settings', 'reportSchedule'),
        { enabled, email: email.trim(), reportType, dayOfWeek, updatedAt: serverTimestamp() },
        { merge: true }
      );
    }, t('settings.reportScheduleSaved'));
  }

  return (
    <SettingsSection
      id="settings-report-schedule"
      lazy
      title={t('settings.reportSchedule')}
      description={t('settings.reportScheduleHint')}
      loading={loading}
    >
      {(visible) =>
        visible ? (
          <>
            <SettingsFirestoreError message={error ?? undefined} />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={enabled}
                disabled={!canEdit || !online}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              {t('settings.reportScheduleEnabled')}
            </label>
            <div>
              <Label htmlFor="report-schedule-email">{t('settings.reportScheduleEmail')}</Label>
              <Input
                id="report-schedule-email"
                type="email"
                className="mt-1 max-w-md"
                value={email}
                disabled={!canEdit || !online}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="report-schedule-type">{t('reports.reportType')}</Label>
              <Select
                className="mt-1 max-w-md"
                value={reportType}
                disabled={!canEdit || !online}
                onValueChange={(v) => setReportType(v as ReportType)}
                options={SCHEDULE_REPORT_TYPES.map((id) => ({
                  value: id,
                  label: t(
                    `reports.types.${id === 'trial_balance' ? 'trialBalance' : id === 'profit_loss' ? 'profitLoss' : id === 'balance_sheet' ? 'balanceSheet' : id === 'ar_aging' ? 'arAging' : 'apAging'}`
                  )
                }))}
              />
            </div>
            <div>
              <Label htmlFor="report-schedule-day">{t('settings.reportScheduleDay')}</Label>
              <Select
                className="mt-1 max-w-md"
                value={String(dayOfWeek)}
                disabled={!canEdit || !online}
                onValueChange={(v) => setDayOfWeek(Number(v))}
                options={DAY_KEYS.map((key, i) => ({
                  value: String(i),
                  label: t(`settings.weekdays.${key}`)
                }))}
              />
            </div>
            {canEdit && (
              <Button variant="secondary" disabled={!online || saving} onClick={() => void save()}>
                {t('common.save')}
              </Button>
            )}
          </>
        ) : null
      }
    </SettingsSection>
  );
}
